/**
 * Turns a profile as it came back from storage into the plain observation the
 * setup revision verifies. The DTO stops here: the verification domain never
 * learns how a profile is serialized.
 */

import { collectConnectorSetupFields } from '../../../shared/lib/hooks/parameterAddition/connectorSetupFields';
import { computeSetupFingerprint } from '../../../shared/lib/setupRevision';
import { getSettingsDtoConnectorConfiguration } from './settingsDtoFamilyDestination';
import type { PcfSettingsDto } from '../../../shared/api/pipeex/pcfSettingsClient';
import type {
  ProfileReadBackObservation,
  SetupDocumentIdentity,
} from '../../../shared/lib/setupRevision';

/** Nothing was stored that could be compared - not "stored as something else". */
const unreadableProfile: ProfileReadBackObservation = { target: null, setupFingerprint: null };

/**
 * Stands in when no Revit document identifies the setup. Both sides of one
 * checkpoint share the identity, so an unknown document must narrow the
 * comparison to the setup values rather than disable it.
 */
const unidentifiedDocument: SetupDocumentIdentity = {
  projectKey: '',
  documentTitle: '',
  documentPath: '',
};

export const observeProfileConnectorSetup = (
  dto: PcfSettingsDto,
  identity: SetupDocumentIdentity | null
): ProfileReadBackObservation => {
  const configuration = getSettingsDtoConnectorConfiguration(dto);
  if (!configuration) {
    return unreadableProfile;
  }

  const { parameterDestination, parameterBindingKind } = configuration;
  if (!parameterDestination || !parameterBindingKind) {
    return unreadableProfile;
  }

  return {
    target: { destination: parameterDestination, bindingKind: parameterBindingKind },
    setupFingerprint: computeSetupFingerprint({
      identity: identity ?? unidentifiedDocument,
      parameterDestination,
      parameterBindingKind,
      setupFields: collectConnectorSetupFields(configuration),
    }),
  };
};
